// /frontend/dashboard/src/utils/debugGate.js
// Shared debug gate for all dashboard frontend files.
//
// English:
// - Logging is FORCE-OFF in production-safe mode.
// - ?debug=1 and localStorage.dashboard_debug are intentionally ignored
//   because previous log floods could hang the browser.
//
// ไทย:
// - บังคับปิด log ในโหมด production-safe
// - ?debug=1 และ localStorage.dashboard_debug จะถูก ignore โดยตั้งใจ
//   เพราะก่อนหน้านี้ log flood ทำให้ browser ค้างได้
//
// Emergency production behavior:
// English: When debug is OFF, suppress noisy console.log/info/debug/warn globally
// to prevent browser lockups caused by log floods. console.error remains untouched.
// You can temporarily bypass suppression with ?console=1 or:
// localStorage.setItem('dashboard_console_passthrough', '1')
//
// ไทย: เมื่อ debug ปิดอยู่ จะ suppress console.log/info/debug/warn แบบ global
// เพื่อป้องกัน browser ค้างจาก log flood โดยจะไม่แตะ console.error
// หากต้องการ bypass ชั่วคราว ใช้ ?console=1 หรือ:
// localStorage.setItem('dashboard_console_passthrough', '1')

const NOOP = () => {};
let debugCount = 0;
let consolePatchApplied = false;

function safeGetSearchParam(name) {
  try {
    if (typeof window === 'undefined' || !window.location) return null;
    return new URLSearchParams(window.location.search).get(name);
  } catch (_error) {
    return null;
  }
}

function safeGetLocalStorageItem(key) {
  try {
    if (typeof localStorage === 'undefined') return null;
    return localStorage.getItem(key);
  } catch (_error) {
    return null;
  }
}

function resolveDebugEnabled() {
  try {
    // English: Force frontend guarded debug OFF in all cases.
    // The dashboard previously produced enough console output to hang the browser,
    // so ?debug=1 and localStorage.dashboard_debug must not re-enable logs.
    //
    // ไทย: บังคับปิด guarded debug ของฝั่ง frontend ทุกกรณี
    // เพราะก่อนหน้านี้ dashboard ปล่อย console จำนวนมากจน browser ค้าง
    // ดังนั้น ?debug=1 และ localStorage.dashboard_debug ต้องไม่สามารถเปิด log ได้
    return false;
  } catch (_error) {
    return false;
  }
}

function resolveConsolePassthroughEnabled() {
  try {
    const urlFlag = safeGetSearchParam('console');

    // URL override
    if (urlFlag === '1') return true;
    if (urlFlag === '0') return false;

    const flag = safeGetLocalStorageItem('dashboard_console_passthrough');

    if (flag === '1') return true;
    if (flag === '0') return false;

    return false;
  } catch (_error) {
    return false;
  }
}

// Resolved once at module load — stable for the page lifetime.
// English: Reload the page after toggling the flag to pick up the new value.
// ไทย: reload หน้าหลังเปลี่ยน flag เพื่อให้ค่าใหม่มีผล
export const DASHBOARD_DEBUG = resolveDebugEnabled();
export const DASHBOARD_CONSOLE_PASSTHROUGH = resolveConsolePassthroughEnabled();

const ORIGINAL_CONSOLE = {
  log: typeof console !== 'undefined' && typeof console.log === 'function' ? console.log.bind(console) : NOOP,
  info: typeof console !== 'undefined' && typeof console.info === 'function' ? console.info.bind(console) : NOOP,
  warn: typeof console !== 'undefined' && typeof console.warn === 'function' ? console.warn.bind(console) : NOOP,
  debug: typeof console !== 'undefined' && typeof console.debug === 'function' ? console.debug.bind(console) : NOOP,
  error: typeof console !== 'undefined' && typeof console.error === 'function' ? console.error.bind(console) : NOOP,
};

function applyConsoleSuppressionIfNeeded() {
  try {
    if (consolePatchApplied) return;
    if (typeof console === 'undefined') return;

    // Keep normal console behavior when guarded debug is enabled
    if (DASHBOARD_DEBUG) return;

    // Allow emergency bypass for live investigation
    if (DASHBOARD_CONSOLE_PASSTHROUGH) return;

    console.log = NOOP;
    console.info = NOOP;
    console.debug = NOOP;
    console.warn = NOOP;
    consolePatchApplied = true;
  } catch (_error) {
    // Never break app startup because of debug tooling
  }
}

applyConsoleSuppressionIfNeeded();

export const ORIGINAL_DASHBOARD_CONSOLE = ORIGINAL_CONSOLE;

// debugLog — replaces unguarded console.log() calls
export function debugLog(...args) {
  if (!DASHBOARD_DEBUG) return;
  if (debugCount >= 100) return; // prevent log flood
  debugCount += 1;
  // [Debug output disabled — freeze/performance isolation | ปิด debug output ชั่วคราวเพื่อแยกปัญหา freeze/performance]
  // ORIGINAL_CONSOLE.log(...args);
}

// debugWarn — replaces unguarded console.warn() calls that are NOT genuine errors
export function debugWarn(...args) {
  if (!DASHBOARD_DEBUG) return;
  if (debugCount >= 100) return;
  debugCount += 1;
  // [Debug output disabled — freeze/performance isolation | ปิด debug output ชั่วคราวเพื่อแยกปัญหา freeze/performance]
  // ORIGINAL_CONSOLE.warn(...args);
}

// debugInfo — alias for debugLog for callers that prefer "info" semantics
export function debugInfo(...args) {
  if (!DASHBOARD_DEBUG) return;
  if (debugCount >= 100) return;
  debugCount += 1;
  // [Debug output disabled — freeze/performance isolation | ปิด debug output ชั่วคราวเพื่อแยกปัญหา freeze/performance]
  // ORIGINAL_CONSOLE.info(...args);
}
